import { apiClient } from '@/lib/api/api-client'

import type {
  OrganizationSubscription,
  SeatChangeHistoryDto,
  SeatUsageDto,
} from '@scrambl/shared'

export interface SeatPreview {
  organizationId: string
  currentSeats: number
  newSeats: number
  prorationAmount: number
  oldMonthlyAmount: number
  newMonthlyAmount: number
  effectiveDate: string
  lineItems: { description: string; amount: number; proration: boolean }[]
  canChange: boolean
  validationMessage?: string
}

export interface SeatRecalculation {
  totalMembers: number
  pendingInvites: number
  expectedSeatsUsed: number
  actualSeatsUsed: number
  membersWithoutSeats: number
  membersAssignedSeats: number
  summary: string
}

const base = (organizationId: string) =>
  `/subscriptions/organizations/${encodeURIComponent(organizationId)}/seats`

export const subscriptionSeatsApi = {
  usage: async (organizationId: string) =>
    (await apiClient<{ data: SeatUsageDto }>(base(organizationId))).data,
  history: async (organizationId: string, limit: number) =>
    (
      await apiClient<{ data: SeatChangeHistoryDto[] }>(
        `${base(organizationId)}/history?limit=${limit}`,
      )
    ).data,
  preview: async (
    organizationId: string,
    seatCount: number,
  ): Promise<SeatPreview> => ({
    ...(
      await apiClient<{ data: Omit<SeatPreview, 'organizationId'> }>(
        `${base(organizationId)}/preview`,
        {
          method: 'POST',
          body: { seatCount },
        },
      )
    ).data,
    organizationId,
  }),
  update: async (organizationId: string, seats: number, immediate: boolean) =>
    (
      await apiClient<{ data: OrganizationSubscription }>(
        base(organizationId),
        {
          method: 'PUT',
          body: { seats, immediate },
        },
      )
    ).data,
  assign: async (organizationId: string, userId: string, active: boolean) =>
    (
      await apiClient<{ data: SeatUsageDto }>(
        `${base(organizationId)}/assign`,
        {
          method: 'POST',
          body: { userId, active },
        },
      )
    ).data,
  recalculate: async (organizationId: string) =>
    (
      await apiClient<{ data: SeatRecalculation }>(
        `${base(organizationId)}/recalculate`,
        {
          method: 'POST',
        },
      )
    ).data,
}
